import { sum } from 'lodash';
import styled from 'styled-components/macro';
import { Header2 } from '../../shared/Headers';
import { TBody, Td, Th, THead, Tr } from '../../shared/Table';
import { colourYellow10 } from '../../styling/colours';
import { getDimensions, getLetterOrdinalAndLocationForManifold } from './helpers/manifoldHelpers';
import { ManifoldDiagramTable } from './manifold-diagram/ManifoldDiagramWithTable';
import { PdfDataManifold } from './PdfDataResponse';

type Props = {
  manifolds: Array<PdfDataManifold>;
};

const PUMP_ROW_HEIGHT_IN_PX = 77;
const NON_PUMP_ROW_HEIGHT_IN_PX = 29;
const APPROXIMATE_SPACE_IN_PX = 1000;
export const ManifoldDetailsTable = ({ manifolds }: Props) => {
  const numberOfManifoldsWithPumps = manifolds.filter(manifold => manifold.pumpId !== null).length;
  const heightOfPumpRowsInPx = numberOfManifoldsWithPumps * PUMP_ROW_HEIGHT_IN_PX;
  const heightOfNonPumpRowsInPx = (manifolds.length - numberOfManifoldsWithPumps) * NON_PUMP_ROW_HEIGHT_IN_PX;
  const displayMode =
    heightOfPumpRowsInPx + heightOfNonPumpRowsInPx > APPROXIMATE_SPACE_IN_PX ? 'Asterisk' : 'FullText';

  const manifoldData = manifolds.map(manifold => {
    const size = sum(manifold.rooms.map(room => room.numberOfPorts));
    const [manifoldName, location] = getLetterOrdinalAndLocationForManifold(manifold.name);

    return {
      id: manifold.manifoldId,
      name: manifoldName,
      location,
      size,
      dimensions: getDimensions(
        size,
        manifold.rooms.map(room => room.pipeDiameterInMillimetres),
        manifold.pumpId,
        displayMode,
      ),
    };
  });

  return (
    <ManifoldDetailsContainer>
      <Header2>Manifold Details</Header2>
      <DetailsTable>
        <THead>
          <Tr>
            <Th>Manifold</Th>
            <Th>Location</Th>
            <Th>Size</Th>
            <AccessColumnFixedWidth>Recommended Minimum Access Dimensions (mm)</AccessColumnFixedWidth>
          </Tr>
        </THead>
        <TBody>
          {manifoldData.map(({ id, name, location, size, dimensions }) => (
            <Tr key={id}>
              <YellowTd>{name}</YellowTd>
              <Td>{location}</Td>
              <Td>{size}-port</Td>
              <Td>{dimensions}</Td>
            </Tr>
          ))}
        </TBody>
      </DetailsTable>
      {displayMode === 'Asterisk' && <p>* including pump & blending valve assembly</p>}
    </ManifoldDetailsContainer>
  );
};

const AccessColumnFixedWidth = styled(Th)`
  width: 160px;
`;

const ManifoldDetailsContainer = styled.div`
  width: 100%;
`;

const DetailsTable = styled(ManifoldDiagramTable)`
  table-layout: auto;
  width: 100%;
`;

const YellowTd = styled(Td)`
  background-color: ${colourYellow10};
`;
